import React, { useContext, useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Container,
  Grid,
  Toolbar,
  Drawer,
  MenuItem,
  IconButton,
  Paper,
  MenuList,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import useWindowScrollPosition from "@rooks/use-window-scroll-position";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import "./SecondNavbar.css";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { homeContext } from "pages/HomeContext";
import { imgPath } from "methods/img";
import { Category } from "types/Root";
import { LocalNavLink } from "hooks/useLocalNavigate";

let Color: string | undefined = "";
let MainColor: string | undefined = "";
function SubMenu({ title, link }: PropsType) {
  return (
    <MenuItem
      className="subLink"
      sx={{
        py: 1.5,
        background: MainColor || "#000",
        "&:hover": {
          color: Color || "#F19B02",
        },
      }}
      component={LocalNavLink}
      to={link}
    >
      {title}
    </MenuItem>
  );
}

function SecondNavbar() {
  const [t, i18n] = useTranslation();
  const { homeData } = useContext(homeContext);
  const cat: Category[] = [...(homeData?.Categories || [])];
  Color = homeData?.SiteColor.secondaryColor;
  MainColor = homeData?.SiteColor.mainColor;
  const theme = useTheme();
  const { scrollY } = useWindowScrollPosition();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [navBackground, setNavBackground] = useState("0");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMediumScreen = useMediaQuery(theme.breakpoints.up("md"));

  useEffect(() => {
    setScrollPosition(scrollY);
  }, [scrollY]);

  useEffect(() => {
    if (scrollPosition > 0) {
      setNavBackground("0");
    } else {
      setNavBackground(isMediumScreen ? "60px" : "120px");
    }
  }, [scrollPosition, isMediumScreen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Services sub menu (mobile)
  const [isServicesSubMenuOpen, setServicesSubMenuOpen] = useState(false);
  const toggleServicesSubMenu = () =>
    setServicesSubMenuOpen(!isServicesSubMenuOpen);

  // Explore Us sub menu (mobile)
  const [isExploreSubMenuOpen, setExploreSubMenuOpen] = useState(false);
  const toggleExploreSubMenu = () =>
    setExploreSubMenuOpen(!isExploreSubMenuOpen);

  // Blogs sub menu (mobile)
  const [isBlogsSubMenuOpen, setBlogsSubMenuOpen] = useState(false);
  const toggleBlogsSubMenu = () => setBlogsSubMenuOpen(!isBlogsSubMenuOpen);

  // Products (hotels) sub menu (mobile)
  const [isHotelSubMenuOpen, setHotelSubMenuOpen] = useState(false);
  const toggleHotelSubMenu = () => setHotelSubMenuOpen(!isHotelSubMenuOpen);

  const servicesCats = [...cat].reverse();
  const hotelCats = homeData?.category_hotels ?? [];
  const shippingCats = homeData?.shippings ?? [];

  // "All Product" هو آخر عنصر في القائمة؛ لو مفيش نرجع للأول
  const lastOf = <T,>(arr: T[]) => arr[arr.length - 1] ?? arr[0];
  const servicesTarget = lastOf(servicesCats);
  const hotelsTarget = lastOf(hotelCats);
  const shippingTarget = lastOf(shippingCats);

  const linkHover = {
    "&:hover": {
      color: homeData?.SiteColor.secondaryColor || "#F19B02",
    },
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        top: navBackground,
        transition: ".2s all ease",
        background: "background",
        color: "primary.main",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Grid container>
            <Grid
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              item
              md={2}
            >
              <Box
                component={LocalNavLink}
                to={""}
                display={"flex"}
                flexDirection={"row"}
                marginY={1}
              >
                <img
                  src={imgPath(homeData?.siteInformation.logo)}
                  alt="logo"
                  width={"130px"}
                />
              </Box>
            </Grid>
            <Grid
              flexDirection={"row"}
              item
              md={10}
              sx={{
                display: { md: "flex", xs: "none" },
                justifyContent: "end",
              }}
            >
              <ul className="secNavbar">
                {/* 1) Home */}
                <li>
                  <Typography
                    component={LocalNavLink}
                    className={"link"}
                    sx={linkHover}
                    to={""}
                  >
                    {homeData?.siteInformation.home}
                  </Typography>
                </li>

                {/* 2) About Us */}
                <li>
                  <Typography
                    component={LocalNavLink}
                    className={"link"}
                    sx={linkHover}
                    to={"/about"}
                  >
                    {homeData?.siteInformation.about_us}
                  </Typography>
                </li>

                {/* 3) Explore Us */}
                <li>
                  <Typography
                    component={LocalNavLink}
                    to={
                      shippingTarget ? `Services/${shippingTarget.id}` : ""
                    }
                    className={"link"}
                    sx={linkHover}
                  >
                    {homeData?.siteInformation.explorer_us}
                  </Typography>
                  <Paper className="subMenu">
                    <MenuList>
                      {shippingCats.map((item) => (
                        <SubMenu
                          key={item.id}
                          title={item.name}
                          link={`Services/${item.id}`}
                        />
                      ))}
                    </MenuList>
                  </Paper>
                </li>

                {/* 4) Products */}
                <li>
                  <Typography
                    component={LocalNavLink}
                    to={hotelsTarget ? `products/${hotelsTarget.id}` : ""}
                    className={"link"}
                    sx={linkHover}
                  >
                    {homeData?.siteInformation.hotel_us}
                  </Typography>
                  <Paper className="subMenu">
                    <MenuList>
                      {hotelCats.map((item) => (
                        <SubMenu
                          key={item.id}
                          title={item.title}
                          link={`products/${item.id}`}
                        />
                      ))}
                    </MenuList>
                  </Paper>
                </li>

                {/* 5) Services */}
                {homeData?.siteInformation?.service_us && (
                  <li>
                    <Typography
                      component={LocalNavLink}
                      to={
                        servicesTarget ? `projects/${servicesTarget.id}` : ""
                      }
                      className={"link"}
                      sx={linkHover}
                    >
                      {homeData?.siteInformation.service_us}
                    </Typography>
                    <Paper className="subMenu">
                      <MenuList>
                        {servicesCats.map((item) => (
                          <SubMenu
                            key={item.id}
                            title={item.title}
                            link={`projects/${item.id}`}
                          />
                        ))}
                      </MenuList>
                    </Paper>
                  </li>
                )}

                {/* 6) Blog */}
                <li>
                  <Typography
                    component={LocalNavLink}
                    className={"link"}
                    sx={linkHover}
                    to={`blog/${homeData?.blogs?.[0]?.id ?? ""}`}
                  >
                    {homeData?.siteInformation.blog_us}
                  </Typography>
                  <Paper className="subMenu">
                    <MenuList>
                      {homeData?.blogs.map((item) => (
                        <SubMenu
                          key={item.id}
                          title={item.title}
                          link={`blog/${item.id}`}
                        />
                      ))}
                    </MenuList>
                  </Paper>
                </li>

                {/* 7) Contact Us */}
                <li>
                  <Typography
                    component={LocalNavLink}
                    className={"link"}
                    sx={linkHover}
                    to={"/contact"}
                  >
                    {homeData?.siteInformation.contact_us}
                  </Typography>
                </li>
              </ul>
            </Grid>
          </Grid>

          {/* ================= Mobile ================= */}
          <Grid item md={6} sx={{ display: { md: "none", xs: "block" } }}>
            <Drawer
              anchor="top"
              open={mobileMenuOpen}
              onClose={toggleMobileMenu}
            >
              <Box
                sx={{ color: "primary.main" }}
                role="presentation"
                onKeyDown={toggleMobileMenu}
              >
                {/* Home */}
                <Typography
                  component={LocalNavLink}
                  className={"link_down"}
                  to={"/"}
                >
                  <MenuItem onClick={toggleMobileMenu}>
                    {homeData?.siteInformation.home}
                  </MenuItem>
                </Typography>

                {/* About Us */}
                <Typography
                  component={LocalNavLink}
                  className={"link_down"}
                  to={"/about"}
                >
                  <MenuItem onClick={toggleMobileMenu}>
                    {homeData?.siteInformation.about_us}
                  </MenuItem>
                </Typography>

                {/* Explore Us */}
                {homeData?.siteInformation?.explorer_us && (
                  <>
                    <MenuItem onClick={toggleExploreSubMenu}>
                      {homeData?.siteInformation.explorer_us}
                      {isExploreSubMenuOpen ? (
                        <KeyboardArrowUpIcon sx={{ fontWeight: 600, ml: 2 }} />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ fontWeight: 600, ml: 2 }}
                        />
                      )}
                    </MenuItem>
                    {isExploreSubMenuOpen && (
                      <Box
                        sx={{
                          backgroundColor: "primary.main",
                          mx: 1,
                          borderRadius: "10px",
                        }}
                      >
                        {shippingCats.map((item) => (
                          <Typography
                            component={LocalNavLink}
                            key={item.id}
                            onClick={() => {
                              toggleMobileMenu();
                              toggleExploreSubMenu();
                            }}
                            to={`Services/${item.id}`}
                          >
                            <MenuItem sx={{ color: "#fff" }}>
                              {item.name}
                            </MenuItem>
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </>
                )}

                {/* Products */}
                {homeData?.siteInformation?.hotel_us && (
                  <>
                    <MenuItem onClick={toggleHotelSubMenu}>
                      {homeData?.siteInformation.hotel_us}
                      {isHotelSubMenuOpen ? (
                        <KeyboardArrowUpIcon sx={{ fontWeight: 600, ml: 2 }} />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ fontWeight: 600, ml: 2 }}
                        />
                      )}
                    </MenuItem>
                    {isHotelSubMenuOpen && (
                      <Box
                        sx={{
                          backgroundColor: "primary.main",
                          mx: 1,
                          borderRadius: "10px",
                        }}
                      >
                        {hotelCats.map((item) => (
                          <Typography
                            component={LocalNavLink}
                            key={item.id}
                            onClick={() => {
                              toggleMobileMenu();
                              toggleHotelSubMenu();
                            }}
                            to={`products/${item.id}`}
                          >
                            <MenuItem sx={{ color: "#fff" }}>
                              {item.title}
                            </MenuItem>
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </>
                )}

                {/* Services */}
                {homeData?.siteInformation?.service_us && (
                  <>
                    <MenuItem onClick={toggleServicesSubMenu}>
                      {homeData?.siteInformation.service_us}
                      {isServicesSubMenuOpen ? (
                        <KeyboardArrowUpIcon sx={{ fontWeight: 600, ml: 2 }} />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ fontWeight: 600, ml: 2 }}
                        />
                      )}
                    </MenuItem>
                    {isServicesSubMenuOpen && (
                      <Box
                        sx={{
                          backgroundColor: "primary.main",
                          mx: 1,
                          borderRadius: "10px",
                        }}
                      >
                        {servicesCats.map((item) => (
                          <Typography
                            component={LocalNavLink}
                            key={item.id}
                            onClick={() => {
                              toggleMobileMenu();
                              toggleServicesSubMenu();
                            }}
                            to={`projects/${item.id}`}
                          >
                            <MenuItem sx={{ color: "#fff" }}>
                              {item.title}
                            </MenuItem>
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </>
                )}

                {/* Blog */}
                {homeData?.siteInformation.blog_us && (
                  <>
                    <MenuItem onClick={toggleBlogsSubMenu}>
                      {homeData?.siteInformation.blog_us}
                      {isBlogsSubMenuOpen ? (
                        <KeyboardArrowUpIcon sx={{ fontWeight: 600, ml: 2 }} />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ fontWeight: 600, ml: 2 }}
                        />
                      )}
                    </MenuItem>
                    {isBlogsSubMenuOpen && (
                      <Box
                        sx={{
                          backgroundColor: "primary.main",
                          mx: 1,
                          borderRadius: "10px",
                        }}
                      >
                        {homeData?.blogs.map((item) => (
                          <Typography
                            component={LocalNavLink}
                            key={item.id}
                            onClick={() => {
                              toggleMobileMenu();
                              toggleBlogsSubMenu();
                            }}
                            to={`blog/${item.id}`}
                          >
                            <MenuItem sx={{ color: "#fff" }}>
                              {item.title}
                            </MenuItem>
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </>
                )}

                {/* Contact Us */}
                <Typography
                  component={LocalNavLink}
                  className={"link_down"}
                  to={"/contact"}
                >
                  <MenuItem onClick={toggleMobileMenu}>
                    {homeData?.siteInformation.contact_us}
                  </MenuItem>
                </Typography>
              </Box>
            </Drawer>

            {/* Mobile Menu Icon */}
            <IconButton
              color="inherit"
              aria-label="open mobile menu"
              edge="start"
              onClick={toggleMobileMenu}
              sx={{ display: { md: "none" } }}
            >
              <MenuIcon />
            </IconButton>
          </Grid>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

type PropsType = {
  title: string;
  link: string;
};

export default SecondNavbar;